import { AlertTriangle, Clock, Info, Sparkles } from 'lucide-react';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatPlanAmount } from './pricing-format';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const TONES = {
	destructive: {
		container: 'border-destructive/40 bg-destructive/10 text-foreground',
		icon: AlertTriangle,
		iconWrap: 'bg-destructive/15 text-destructive'
	},
	info: {
		container: 'border-border bg-muted/60 text-foreground',
		icon: Info,
		iconWrap: 'bg-accent text-accent-foreground'
	},
	primary: {
		container: 'border-primary/30 bg-primary-soft text-primary-soft-foreground',
		icon: Clock,
		iconWrap: 'bg-primary/15 text-primary-soft-foreground'
	},
	action: {
		container: 'border-action/20 bg-action-soft text-action-soft-foreground',
		icon: AlertTriangle,
		iconWrap: 'bg-action text-action-foreground'
	}
};

/**
 * Payment-nudge stage during the trial, ported from the Lovable design
 * (fresh-dashboard `src/lib/lifecycle.ts` `deriveNudgeStage`): days 1–10
 * quiet, days 11–24 soft nudge, days 25–30 hard nudge.
 *
 * Keyed on days *remaining* rather than days elapsed — identical maths on
 * the canonical 30-day trial, but it stays correct for compressed trials
 * (the `billing-*` seeds start their trial "today", so elapsed-day maths
 * would read every seeded trial as day 0 and never escalate).
 */
const deriveTrialNudge = ({ daysLeft, trialStart, trialEnd }) => {
	const spanMs = trialStart && trialEnd ? new Date(trialEnd) - new Date(trialStart) : null;
	const trialDays = spanMs > 0 ? Math.round(spanMs / MS_PER_DAY) : 30;
	const day = Math.min(trialDays, Math.max(1, trialDays - daysLeft));
	if (daysLeft >= 20) return { stage: 'none', day, trialDays };
	if (daysLeft >= 6) return { stage: 'soft', day, trialDays };
	return { stage: 'hard', day, trialDays };
};

/**
 * Top-of-page banner driven by `useBillingStatus`. One banner at a time,
 * picked in priority order (expired → cancel-at-end → trial nudge).
 * Returns null when none of those branches apply — including a trialing
 * shop with a card already on file (the design's "joined" state: the trial
 * converts by itself, and the PlanCard's "Free until {date}" line carries
 * the date) and a healthy active sub past trial.
 *
 * The trial variants mirror the design's `TrialBanner` (fresh-dashboard
 * `src/pages/SettingsBilling.tsx`) — the continue price is the live trial
 * plan price (the months-2–3 offer, £1/month for the current catalogue).
 */
export function TrialStatusBanner({ billing, onAddCard, onResume }) {
	if (!billing) return null;
	const { status, expired, daysLeft, trialStart, trialEnd, cancelAt, isTrialing, hasPaymentMethod, amount, currency } = billing;

	if (expired) {
		return (
			<Banner
				tone="destructive"
				eyebrow="Trial ended"
				title="Your free trial has ended"
				body="Subscribe now to switch your storefront, channels and orders back on — still just £1/month for the first 2 months."
				cta={onAddCard ? { label: 'Add a card to reactivate', onClick: onAddCard, variant: 'action' } : null}
			/>
		);
	}

	if (cancelAt) {
		const when = format(new Date(cancelAt), 'd MMM yyyy');
		return (
			<Banner
				tone="info"
				eyebrow="Cancelling"
				title={`Subscription ends on ${when}`}
				body="You'll keep full access until then. Resume any time before the end date to stay on Unify."
				cta={onResume ? { label: 'Resume subscription', onClick: onResume, variant: 'default' } : null}
			/>
		);
	}

	if (isTrialing && daysLeft != null && !hasPaymentMethod) {
		const { stage, day, trialDays } = deriveTrialNudge({ daysLeft, trialStart, trialEnd });
		const monthly = `${formatPlanAmount(amount, currency) ?? '£1'}/month`;
		const daysLeftLabel = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;

		if (stage === 'hard') {
			return (
				<Banner
					tone="action"
					eyebrow={daysLeftLabel}
					title={
						daysLeft === 0 ? 'Your store pauses today' : `Your store pauses in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`
					}
					body={`Continue for ${monthly} to keep your storefront, channels and orders running.`}
					cta={onAddCard ? { label: 'Add a card', onClick: onAddCard, variant: 'action' } : null}
				/>
			);
		}

		if (stage === 'soft') {
			return (
				<Banner
					tone="primary"
					icon={Sparkles}
					eyebrow="Keep the momentum"
					title={`You're on day ${day} of ${trialDays} — things look good`}
					body={`Continue for ${monthly} and keep things rolling. Cancel any time.`}
					cta={onAddCard ? { label: 'Add a card', onClick: onAddCard, variant: 'outline' } : null}
				/>
			);
		}

		return (
			<Banner
				tone="primary"
				eyebrow="Free trial"
				title={`${daysLeftLabel} — click below to continue ${monthly}`}
				body="No charge until your free trial ends. Cancel any time."
				cta={onAddCard ? { label: 'Add a card', onClick: onAddCard, variant: 'outline' } : null}
			/>
		);
	}

	if (status === 'no_subscription') {
		return (
			<Banner
				tone="primary"
				eyebrow="Subscribe"
				title="Start your Unify subscription"
				body="You're set up but not subscribed yet. Pick a plan to switch on channels and start syncing your storefront."
				cta={onAddCard ? { label: 'Subscribe to Unify', onClick: onAddCard, variant: 'action' } : null}
			/>
		);
	}

	return null;
}

function Banner({ tone, icon, eyebrow, title, body, cta }) {
	const config = TONES[tone];
	const Icon = icon ?? config.icon;
	return (
		<div className={cn('flex items-start gap-4 rounded-2xl border p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]', config.container)}>
			<div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', config.iconWrap)}>
				<Icon className="h-5 w-5" />
			</div>
			<div className="min-w-0 flex-1 space-y-1">
				<div className="text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80">{eyebrow}</div>
				<div className="text-base font-bold">{title}</div>
				<div className="text-sm opacity-90">{body}</div>
			</div>
			{cta ? (
				<Button variant={cta.variant} onClick={cta.onClick} className="shrink-0">
					{cta.label}
				</Button>
			) : null}
		</div>
	);
}
